import cx from 'classnames';
import format from 'date-fns/format';
import React, { useEffect, useMemo, useState } from 'react';
import { DropdownButton } from 'react-bootstrap';
import Dropdown from 'react-bootstrap/Dropdown';
import Table from 'react-bootstrap/Table';
import 'react-datepicker/dist/react-datepicker.css';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useNavigate } from 'react-router-dom';
import { usePrevious } from 'react-use';
import { icons } from '../../assets';
import Switch from '../../components/admin/switch';
import Button from '../../components/Button/Button';
import ArrowDownIcon from '../../components/icons/ArrowDownIcon';
import CloseCircleIcon from '../../components/icons/closeCircleIcon';
import Loader from '../../components/Loader/Loader';
import {
  INVOICE_ICON,
  PAYMENT_NOTE_ICON
} from '../../config/constant/assetConstant';
import { PER_PAGE_OPTIONS } from '../../constants/projectConstants';
import { useUserContext } from '../../contexts/UserContext';
import {
  apiDownloadTransactionHistory,
  apiGetOrganizationBalance,
  apiGetPreviewAndMainRenders,
  apiGetTransactionHistoryStatus
} from '../../helpers/api';
import { windowOpen } from '../../helpers/jsHelper';
import { errorToast, warningToast } from '../../helpers/toastHelper';
import { isValidHttpURL } from '../../helpers/urlHelper';
import { useTransactionsHistory } from '../../hooks/useTransactionsHistory';
import { useTransactionsHistoryEmailList } from '../../hooks/useTransactionsHistoryEmailList';
import RenderDetails from '../../screens/RenderDetails';
import { DropdownMenu } from '../../screens/subscriptionComponents/DropdownMenu';
import { SkeletonUI } from '../../skeletonLoaderUI/SkeletonLoaderUI';
import { download } from '../DownloadFileController/downloadFile';
import { Pagination } from '../Pagination/Pagination';
import { CalendarDropdownContent } from './CalendarDropdownContent';
import { Placeholder } from './Placeholder';
import styles from './TransactionHistory.module.scss';
import { TransactionNoteDialog } from './TransactionNoteDialog';
import { SETTINGS_TAB } from '../../constants/constants';
import { ElipsisTooltip } from "../ElipsisTooltip";

const { TransactionDetailsIcon, SearchBarIcon } = icons;

const TRANSACTION_STATUS = {
  IN_PROGRESS: 'In Progress',
  SUCCESS: 'Paid',
  FAILED: 'Failed',
  OPEN: 'Open',
};
const TRANSACTION_STATUS_ID = {
  1: TRANSACTION_STATUS.IN_PROGRESS,
  2: TRANSACTION_STATUS.SUCCESS,
  3: TRANSACTION_STATUS.FAILED,
  4: TRANSACTION_STATUS.OPEN,
};

const getTransactionStatus = (status) => {
  if (!status) return status;
  return TRANSACTION_STATUS_ID[status];
};

const TRANSACTION_TYPE = {
  ALL: '',
  FAILED: 'FAILED',
  AMOUNT_DEDUCTED: 'DED',
  AMOUNT_ADDED: 'ADD',
  AMOUNT_BORROWED: 'BORROWED',
  RENDERED_IMAGES: 'RENDERED_IMAGES',
  REQUESTS: 'REQUESTS',
  CREDIT_PURCHASED: 'CREDIT_PURCHASED',
  REFUND: 'REFUND',
  SUBSCRIPTION: 'SUBSCRIPTION',
  FABRIC_SEAMLESS_REPEATS: 'FABRIC_SEAMLESS_REPEATS',
  VIDEO: 'VIDEO',
  THREE_D_MODEL_VARIATION: '3D_MODEL_VARIATION',
  THREE_D_MODEL: '3D_MODEL',
  AMOUNT_FAILED: 'AMOUNT_FAILED',
  MANUALLY_ADDED: 'MANUALLY_ADDED',
  MANUALLY_DEDUCTED: 'MANUALLY_DEDUCTED',
};

const TRANSACTION_REASON = {
  RETURN: 'RETURN',
};

const makeTypeMatchable = (value) => {
  return value.toLowerCase().replace('_', ' ').replace('three', '3');
};

const getFormattedTransactionType = (type) => {
  if (!type) return type;
  const isExists = Object.entries(TRANSACTION_TYPE).find(
    ([key, value]) =>
      makeTypeMatchable(key) === makeTypeMatchable(type) ||
      makeTypeMatchable(value) === makeTypeMatchable(type)
  );
  return makeTypeMatchable(isExists?.[1]);
};

const POSITIVE_TRANSACTIONS = [
  TRANSACTION_TYPE.AMOUNT_ADDED,
  TRANSACTION_TYPE.CREDIT_PURCHASED,
  TRANSACTION_TYPE.REFUND,
  TRANSACTION_TYPE.AMOUNT_BORROWED,
  TRANSACTION_TYPE.RENDERED_IMAGES,
];

const placeholderList = [...Array(10)];

const transactionTypeOptionList = [
  { label: 'All transactions', value: '' },
  // { label: 'Failed', value: TRANSACTION_TYPE.FAILED },
  { label: 'Rendered images', value: TRANSACTION_TYPE.RENDERED_IMAGES },
  // { label: 'Requests', value: TRANSACTION_TYPE.REQUESTS },
  // { label: 'Credit purchased', value: TRANSACTION_TYPE.CREDIT_PURCHASED },
  // { label: 'Refund', value: TRANSACTION_TYPE.REFUND },
  { label: 'Deducted', value: TRANSACTION_TYPE.AMOUNT_DEDUCTED },
  { label: 'Added', value: TRANSACTION_TYPE.AMOUNT_ADDED },
  { label: 'Subscription', value: TRANSACTION_TYPE.SUBSCRIPTION },
  // { label: 'Borrowed', value: TRANSACTION_TYPE.AMOUNT_BORROWED },
];

const transactionLabelMap = transactionTypeOptionList
  .filter((o) => Boolean(o.value))
  .reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});

const invoiceTypeOptionList = [
  { label: 'All', value: '' },
  { label: 'Manually added', value: 'true' },
  { label: 'Others', value: 'false' },
];

let initial = true;
export const TransactionHistory = ({ activeTab }) => {
  const navigate = useNavigate();
  const { currentUser } = useUserContext();
  const organisationId = currentUser.organization;
  const [balance, setBalance] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [transactionType, setTransactionType] = useState(TRANSACTION_TYPE.ALL);
  const [invoiceType, setInvoiceType] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [renderData, setRenderData] = useState(null);
  const [historyDownloading, setHistoryDownloading] = useState(false);
  const [layoutType, toggleLayout] = useState(true);
  const [transHistoryStatus, setTransHistoryStatus] = useState({ is_old: false, is_new: true });
  const isOldLayout = layoutType === false;

  const isTransactionsTabActive = activeTab === SETTINGS_TAB.TRANSACTIONS;

  const layoutTypePrevious = usePrevious(layoutType);

  const resetState = () => {
    setSelectedEmail(null);
    setTransactionType(TRANSACTION_TYPE.ALL);
    setInvoiceType('');
    setStartDate(null);
    setEndDate(null);
    setHistoryDownloading(false);
  };

  function addOneDay(date = new Date()) {
    date.setDate(date.getDate() + 1);
    return date;
  }

  const requestParams = useMemo(() => {
    if(!isTransactionsTabActive) return;
    if (layoutType !== layoutTypePrevious) resetState();

    const params = {
      ...(invoiceType !== '' && { is_manually: invoiceType }),
      ...(transactionType && { trans_type: transactionType }),
      ...(startDate && { created_at__gte: addOneDay(new Date(startDate)).toISOString() }),
      ...(endDate && { created_at__lte: addOneDay(new Date(endDate)).toISOString() }),
      ...(isOldLayout ? { is_new: false } : { is_new: true }),
    };

    return {
      ...params,
      ...(selectedEmail && { created_by_email: selectedEmail }),
    };
  }, [transactionType, invoiceType, selectedEmail, startDate, endDate, organisationId, layoutType, isTransactionsTabActive]);

  const {
    list: transactionList,
    prevList,
    loading,
    total,
    perPage,
    page,
    search,
    onSearchChange,
    onPageChange,
    onPerPageChange,
    dataLoadedOnce,
    hasData,
  } = useTransactionsHistory({ requestParams });

  const getTransactionHistoryStatus = () => {
    apiGetTransactionHistoryStatus()
    .then(async response => {
      if(response.status) {
        setTransHistoryStatus(response?.data);
      }
    })
  }

  useEffect(() => {
    if(!isTransactionsTabActive) return;
    getTransactionHistoryStatus();
    apiGetOrganizationBalance().then((res) => {
      setBalance(res);
    });
    toggleLayout(true);
  }, [organisationId, isTransactionsTabActive]);

  const handleDownloadClick = async () => {
    setHistoryDownloading(true);

    try {
      const result = await apiDownloadTransactionHistory(requestParams);

      if (!result?.file) {
        setHistoryDownloading(false);
        return;
      }

      // download(result.file);
      await download({ fileUrl: result.file });
      setHistoryDownloading(false);
    } catch (e) {
      setHistoryDownloading(false);
    }
  };

  const toggleTransactionType = (type) => {
    transactionType === type ? setTransactionType(null) : setTransactionType(type);
  };

  const toggleInvoiceType = (type) => {
    invoiceType === type ? setInvoiceType(null) : setInvoiceType(type);
  };

  const toggleSelectedEmail = (email) => {
    selectedEmail === email ? setSelectedEmail(null) : setSelectedEmail(email);
  };

  const onDateChagne = ({ name, value }) => {
    name === 'startDate' ? setStartDate(value) : setEndDate(value);
  };

  // const canAddCredits = async () => {
  //   const userDetails = await apiGetUserDetails();
  //   if (userDetails) {
  //     const { country, state, city } = userDetails;
  //     if (country && (state || city)) {
  //       navigate(ADD_CREDITS_ROUTE);
  //     } else {
  //       warningToast('Please fill your basic details to proceed with Add Credits.');
  //     }
  //   }
  // };

  const transactionTypeOption = transactionTypeOptionList.find((t) => t.value === transactionType);

  const dateFilterLabel = useMemo(() => {
    const longFormat = 'MMM dd, yyyy';

    if (startDate && endDate) {
      const start = format(startDate, 'MMM dd');
      const end = format(endDate, longFormat);
      return `${start} - ${end}`;
    } else if (startDate) {
      return `${format(startDate, longFormat)} - ...`;
    } else if (endDate) {
      return `... - ${format(endDate, longFormat)}`;
    }
  }, [startDate, endDate]);

  const invoiceTypeOption = invoiceTypeOptionList.find((t) => t.value === invoiceType);

  const filtersActive = Boolean(transactionType || selectedEmail || dateFilterLabel || invoiceType);
  const showPlaceholders = loading || !transactionList.length;

  const PlaceholderLoadingComponent = loading ? SkeletonUI : Placeholder;

  if (loading) return <Loader loading backdrop />;

  return (
    <div className={styles.root}>
      {/* <div className={styles.header}>
        <h2>Your historical </h2>
        <div className="d-flex gap-3"> */}
      {/* <PermissionArea name={PERMISSION.PURCHASE_CREDIT}>
            <Button
              color="warning"
              size="xlg"
              iconBefore={<PlusIcon />}
              onClick={canAddCredits}>
              Add Credits
            </Button>
          </PermissionArea> */}

      {/* <Button
            color="warning"
            size="xlg"
            iconBefore={<DownloadIcon />}
            onClick={handleDownloadClick}
            loading={historyDownloading}
            disabled={transactionList.length === 0}
          >
            Download
          </Button> */}
      {/* </div>
      </div> */}

      <div
        className={cx(
          styles.cardList,
          !loading && isOldLayout ? 'justify-content-between' : 'justify-content-end'
        )}>
        {!loading && isOldLayout && (
          <div className={styles.card}>
            <span>Balance</span>
            <h2>$ {balance?.amount}</h2>
          </div>
        )}
        {transHistoryStatus?.is_old && (
          <label className={styles.layoutSwitch}>
            <span className={styles.switchTitle}>Switch layouts</span>
            <div className={styles.switch}>
              <span className={styles.switchText}>Old</span>
              <Switch checked={layoutType} onToggle={() => toggleLayout(!layoutType)} />
              <span className={styles.switchText}>New</span>
            </div>
          </label>
        )}
      </div>

      <div className={styles.container}>
        <div className="d-flex align-items-center justify-content-between gap-3 mb-4 pb-2">
          <h2>Your historical </h2>
          <Button
            color="warning"
            size="xlg"
            // iconBefore={<DownloadIcon />}
            onClick={handleDownloadClick}
            loading={historyDownloading}
            disabled={transactionList.length === 0}>
            Download all
          </Button>
        </div>

        {isOldLayout ? (
          <OldUserTableLayout
            loading={loading}
            transactionList={transactionList}
            setRenderData={setRenderData}
            filtersActive={filtersActive}
            toggleTransactionType={toggleTransactionType}
            transactionType={transactionType}
            transactionTypeOption={transactionTypeOption}
            toggleSelectedEmail={toggleSelectedEmail}
            selectedEmail={selectedEmail}
            startDate={startDate}
            endDate={endDate}
            onChange={onDateChagne}
            onDateChagne={onDateChagne}
            dateFilterLabel={dateFilterLabel}
            PlaceholderLoadingComponent={PlaceholderLoadingComponent}
            placeholderList={placeholderList}
            showPlaceholders={showPlaceholders}
          />
        ) : (
          <NewUserTableLayout
            loading={loading}
            transactionList={transactionList}
            filtersActive={filtersActive}
            invoiceTypeOptionList={invoiceTypeOptionList}
            toggleInvoiceType={toggleInvoiceType}
            invoiceType={invoiceType}
            invoiceTypeOption={invoiceTypeOption}
            toggleSelectedEmail={toggleSelectedEmail}
            selectedEmail={selectedEmail}
            startDate={startDate}
            endDate={endDate}
            onChange={onDateChagne}
            onDateChagne={onDateChagne}
            dateFilterLabel={dateFilterLabel}
            PlaceholderLoadingComponent={PlaceholderLoadingComponent}
            placeholderList={placeholderList}
            showPlaceholders={showPlaceholders}
          />
        )}
      </div>

      <Pagination
        perPage={perPage}
        currentPage={page}
        totalItems={total}
        onPageChange={onPageChange}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={onPerPageChange}
      />

      {renderData && (
        <RenderDetails
          id={renderData.id}
          onClose={() => setRenderData(null)}
          onDeleteSuccess={() => setRenderData(null)}
          previewProps={{ className: styles.renderBody }}
          sidebarProps={{ className: styles.renderSidebar }}
        />
      )}
    </div>
  );
};

const NewUserTableLayout = ({
  loading,
  transactionList,
  filtersActive,
  toggleSelectedEmail,
  selectedEmail,
  startDate,
  endDate,
  onDateChagne,
  dateFilterLabel,
  PlaceholderLoadingComponent,
  placeholderList,
  showPlaceholders,
  invoiceTypeOptionList,
  invoiceTypeOption,
  toggleInvoiceType,
  invoiceType,
}) => {
  const handleViewInvoice = (trans) => {
    if (!isValidHttpURL(trans.receipt_url)) {
      warningToast('Invalide URL.');
      return;
    }
    windowOpen(trans.receipt_url, '_blank');
  };

  const handleRewardText = (transaction) => {
    if (transaction?.trans_type === TRANSACTION_TYPE.MANUALLY_ADDED && (+transaction?.amount === 0 || transaction?.amount === "-")) {
      return { value: '-', actionButton: false };
    } else {
      const buttonFlag = (transaction?.transaction_reason === TRANSACTION_REASON?.RETURN || transaction?.trans_type === TRANSACTION_TYPE.MANUALLY_ADDED) ? false : true
      return { value: transaction?.transaction_reason === TRANSACTION_REASON?.RETURN ? `$${transaction?.amount}` : transaction?.amount ? `$${transaction?.amount}` : '-', actionButton: buttonFlag };
    }
  };

  const manageReturnStatus = (transaction) => {
    if (transaction?.transaction_reason === TRANSACTION_REASON?.RETURN) {
      return '-';
    } else {
      return getTransactionStatus(transaction.status)
    }
  };

  const handleRefundTooltip = (transaction) => {
    if (transaction?.transaction_reason === TRANSACTION_REASON?.RETURN && (+transaction?.amount !== 0 || transaction?.amount !== "-" || +transaction?.amount)) {
      return "Refund credit"  
    } else {
      return ''
    }
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table striped bordered className={`basic-table ${styles.table}`}>
        <thead className="basic-table-head">
          <tr>
            <th>
              <div>
                <CustomDropdown
                  title="Date & Time"
                  type="calendar"
                  startDate={startDate}
                  endDate={endDate}
                  onChange={onDateChagne}
                />
              </div>
              {filtersActive && <div className="basic-table-specification">{dateFilterLabel}</div>}
            </th>
            <th>
              <CustomDropdown
                title="Invoice #"
                options={invoiceTypeOptionList}
                onChange={toggleInvoiceType}
                selected={invoiceType}
              />
              {filtersActive && (
                <div className="basic-table-specification">{invoiceTypeOption?.label}</div>
              )}
            </th>
            <th>
              <div>Description</div>
              {filtersActive && <div className="basic-table-specification"></div>}
            </th>
            <th>
              <EmailDropdown
                title={'User'}
                onChange={toggleSelectedEmail}
                selected={selectedEmail}
              />
              {filtersActive && <div className="basic-table-specification">{selectedEmail}</div>}
            </th>
            <th>
              <div>Amount</div>
              {filtersActive && <div className="basic-table-specification"></div>}
            </th>
            <th>
              <div>Status</div>
              {filtersActive && <div className="basic-table-specification"></div>}
            </th>
            <th style={{ textAlign: 'center' }}>
              <div></div>
            </th>
          </tr>
        </thead>

        {/* <div className="basic-table-border"> */}
        <tbody className={styles.tableBody}>
          {showPlaceholders &&
            placeholderList.map((_, idx) => (
              <tr key={idx}>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={80} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={121} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={162} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={132} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={41} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={54} />
                </td>
                <td
                  className={styles.tableColumn}
                  style={{ display: 'flex', justifyContent: 'center' }}>
                  <PlaceholderLoadingComponent width={24} height={24} />
                </td>
              </tr>
            ))}
          {showPlaceholders && !loading && (
            <div className={styles.noTransactionsText}>No transactions found</div>
          )}

          {!loading &&
            transactionList.map((transaction, idx) => {
              if (
                !transaction?.is_manually &&
                transaction?.trans_type === TRANSACTION_TYPE.MANUALLY_DEDUCTED &&
                transaction?.collection_method !== 'send_invoice' &&
                getTransactionStatus(transaction.status)?.toLowerCase() === 'open'
              ) {
                return;
              }
              const date = new Date(transaction.created_at);
              const formattedDate = format(date, 'MMM dd, yyyy');

              let transactionActionMenu = [
                { id: 1, title: 'View invoice', icon: null, onClick: handleViewInvoice },
              ];

              const tooltip = transaction?.other_details?.message ||
              transaction?.other_details?.product ||
              transaction?.other_details?.name ||
              '-'

              return (
                <tr key={transaction.id} className={cx(styles[`type${transaction.trans_type}`])}>
                  <td className={styles.tableColumn}>{formattedDate}</td>
                  <td className={styles.tableColumn}>
                    {transaction.is_manually ? 'Manually added' : transaction.invoice_id || '-'}
                  </td>
                  <td className={styles.tableColumn}>
                    {handleRefundTooltip(transaction) ? (
                      <ElipsisTooltip title={handleRefundTooltip(transaction)} alwaysShow={true}>
                        {(props) => (
                          <div style={{ cursor: 'pointer' , color:"blue", width: "15ch", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} {...props}>
                            {tooltip}
                          </div>
                        )}
                      </ElipsisTooltip>
                    ) : (
                      <ElipsisTooltip title={tooltip} alwaysShow={true}>
                        {(props) => (
                          <div style={{ cursor: 'pointer', width: "15ch", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}} {...props}>
                            {tooltip}
                          </div>
                        )}
                      </ElipsisTooltip>
                    )}
                  </td>
                  <td className={styles.tableColumn}>{transaction.created_by_email || '-'}</td>
                  <td className={styles.tableColumn}>{handleRewardText(transaction)?.value}</td>
                  <td className={styles.tableColumn}>
                    {transaction.is_manually ? '-' : manageReturnStatus(transaction)}
                  </td>

                  <td className={styles.tableColumn} style={{ textAlign: 'center' }}>
                    {getTransactionStatus(transaction.status)?.toLowerCase() === 'open' ? (
                      ''
                    ) : handleRewardText(transaction)?.actionButton ? (
                      <DropdownMenu menu={transactionActionMenu} item={transaction} />
                    ) : (
                      ''
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
        {/* </div> */}
      </Table>
    </div>
  );
};

const OldUserTableLayout = ({
  loading,
  transactionList,
  setRenderData,
  filtersActive,
  toggleTransactionType,
  transactionType,
  transactionTypeOption,
  toggleSelectedEmail,
  selectedEmail,
  startDate,
  endDate,
  onDateChagne,
  dateFilterLabel,
  PlaceholderLoadingComponent,
  placeholderList,
  showPlaceholders,
}) => {
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table striped bordered className={`basic-table ${styles.table}`}>
        <thead className="basic-table-head">
          <tr>
            <th>
              <CustomDropdown
                title="Transaction Type"
                options={transactionTypeOptionList}
                onChange={toggleTransactionType}
                selected={transactionType}
              />
              {filtersActive && (
                <div className="basic-table-specification">{transactionTypeOption?.label}</div>
              )}
            </th>
            <th>
              <div>Reason</div>
              {filtersActive && <div className="basic-table-specification"></div>}
            </th>
            <th>
              <EmailDropdown onChange={toggleSelectedEmail} selected={selectedEmail} />
              {filtersActive && <div className="basic-table-specification">{selectedEmail}</div>}
            </th>

            <th>
              <div>
                <CustomDropdown
                  title="Date & Time"
                  type="calendar"
                  startDate={startDate}
                  endDate={endDate}
                  onChange={onDateChagne}
                />
              </div>
              {filtersActive && <div className="basic-table-specification">{dateFilterLabel}</div>}
            </th>
            <th>
              <div>Amount</div>
              {filtersActive && <div className="basic-table-specification"></div>}
            </th>
            <th>
              <div>Balance</div>
              {filtersActive && <div className="basic-table-specification"></div>}
            </th>
            <th style={{ textAlign: 'center' }}>
              <div>Details</div>
              {filtersActive && <div className="basic-table-specification"></div>}
            </th>
          </tr>
        </thead>

        {/* <div className="basic-table-border"> */}
        <tbody className={styles.tableBody}>
          {showPlaceholders &&
            placeholderList.map((_, idx) => (
              <tr key={idx}>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={132} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={80} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={162} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={121} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={41} />
                </td>
                <td className={styles.tableColumn}>
                  <PlaceholderLoadingComponent maxWidth={54} />
                </td>
                <td
                  className={styles.tableColumn}
                  style={{ display: 'flex', justifyContent: 'center' }}>
                  <PlaceholderLoadingComponent width={24} height={24} />
                </td>
              </tr>
            ))}
          {showPlaceholders && !loading && (
            <div className={styles.noTransactionsText}>No transactions found</div>
          )}

          {!loading &&
            transactionList.map((transaction, idx) => {
              const date = new Date(transaction.created_at);
              const formattedDate = format(date, 'MMM dd, yyyy  - HH:mm');
              const transactionAmountColor =
                transaction.amount === 0
                  ? undefined
                  : POSITIVE_TRANSACTIONS.includes(transaction.trans_type)
                  ? 'black'
                  : 'red';

              const transactionSign =
                transaction.amount === 0
                  ? ''
                  : POSITIVE_TRANSACTIONS.includes(transaction.trans_type)
                  ? '+'
                  : '-';

              return (
                <tr key={transaction.id} className={cx(styles[`type${transaction.trans_type}`])}>
                  <td className={styles.tableColumn}>
                    {transactionLabelMap[transaction.trans_type]}
                  </td>
                  <td className={styles.tableColumn}>{transaction.display_name}</td>
                  <td className={styles.tableColumn}>{transaction.created_by_email}</td>
                  <td className={styles.tableColumn}>{formattedDate}</td>
                  <td className={styles.tableColumn} style={{ color: transactionAmountColor }}>
                    {transactionSign}
                    {transaction.amount} $
                  </td>
                  <td
                    className={styles.tableColumn}
                    style={{ color: transaction.remaining_amount < 0 ? 'red' : 'black' }}>
                    {transaction.remaining_amount || 0} $
                  </td>
                  <td className={styles.tableColumn} style={{ textAlign: 'center' }}>
                    <TransactionIcon transaction={transaction} setRenderData={setRenderData} />
                  </td>
                </tr>
              );
            })}
        </tbody>
        {/* </div> */}
      </Table>
    </div>
  );
};

const TransactionIcon = ({ transaction, setRenderData }) => {
  const [loading, setLoading] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  if (isValidHttpURL(transaction.receipt_url)) {
    return (
      <a href={transaction.receipt_url} target="_blank" rel="noreferrer" className={styles.icon}>
        <img src={INVOICE_ICON} />
      </a>
    );
  }

  if (transaction.render) {
    const handleRenderClick = async () => {
      setLoading(true);
      const result = await apiGetPreviewAndMainRenders(
        `sort=-created_at&main_render=${transaction.render}`
      );

      const render = result?.results?.[0];
      if (!render?.id) {
        errorToast('Unable to get render details, please try again');
        setLoading(false);
        return;
      }

      // navigate(getRenderDetailPath(render.id));
      setRenderData(render);
      setLoading(false);
    };

    return (
      <span onClick={handleRenderClick} className={styles.icon}>
        {loading ? (
          <Loader loading position="static" size={16} thickness={2} />
        ) : (
          <TransactionDetailsIcon />
        )}
      </span>
    );
  }

  if (transaction.payment_note) {
    return (
      <>
        <span onClick={() => setNoteOpen(true)} className={styles.icon}>
          <img src={PAYMENT_NOTE_ICON} />
        </span>
        {noteOpen && (
          <TransactionNoteDialog
            onClose={() => setNoteOpen(false)}
            note={transaction.payment_note}
          />
        )}
      </>
    );
  }

  return null;
};

const CustomDropdown = ({
  title,
  options,
  selected,
  onChange,
  loading,
  hasMore,
  fetchMoreData,
  startDate,
  endDate,
  type,
  search,
  onSearchChange,
}) => {
  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: fetchMoreData,
    rootMargin: '0px 0px 120px 0px',
  });

  const renderTitle = (title) => {
    return (
      <>
        {title}
        <span className={styles.arrow}>
          <ArrowDownIcon />
        </span>
      </>
    );
  };

  const isCalendar = type === 'calendar';

  const handleToggle = (nextShow) => {
    if (!nextShow && onSearchChange && search) {
      onSearchChange('');
    }
  };

  return (
    <DropdownButton
      className={styles.tableDropdown}
      title={renderTitle(title)}
      onToggle={handleToggle}>
      {isCalendar && (
        <CalendarDropdownContent onChange={onChange} startDate={startDate} endDate={endDate} />
      )}

      {onSearchChange && (
        <div className={styles.dropdownSearchInput}>
          <input type="text" value={search} onChange={onSearchChange} placeholder="Search" />
          <SearchBarIcon className={styles.searchIcon} />
        </div>
      )}

      {!isCalendar &&
        options.map((option) => {
          const active = selected === option.value;

          return (
            <Dropdown.Item
              key={option.value}
              active={active}
              onClick={active ? undefined : () => onChange(option.value)}>
              {option.label}
              {active && (
                <CloseCircleIcon
                  onClick={() => onChange(option.value)}
                  className={styles.closeIcon}
                />
              )}
            </Dropdown.Item>
          );
        })}
      {loading && (
        <Dropdown.Item
          style={{
            pointerEvents: 'none',
            position: 'relative',
            paddingBottom: '2rem',
            transform: 'translateY(-7px)',
          }}>
          <Loader loading size={20} thickness={2} />
        </Dropdown.Item>
      )}
      {Boolean(search) && !loading && !options.length && (
        <Dropdown.Item disabled>No results</Dropdown.Item>
      )}
      {hasMore && <div ref={sentryRef}></div>}
    </DropdownButton>
  );
};

const EmailDropdown = ({ title = 'Email', onChange, selected }) => {
  const {
    list: memberList,
    loading,
    fetchMoreData,
    hasMore,
    total,
    hasData,
    search,
    onSearchChange,
  } = useTransactionsHistoryEmailList();

  const options = useMemo(() => {
    return memberList.map((member) => ({
      label: member.user.email,
      value: member.user.email,
    }));
  }, [memberList]);

  return (
    <CustomDropdown
      title={title}
      options={options}
      onChange={onChange}
      selected={selected}
      loading={loading}
      hasMore={hasMore}
      fetchMoreData={fetchMoreData}
      search={search}
      onSearchChange={onSearchChange}
    />
  );
};
